import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RequestState } from '@kfolks/types'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card } from '@/shared/components/atoms/ui/card'
import { Input } from '@/shared/components/atoms/ui/input'
import { type ListRequestParams, type RequestDTO, requestCenterApi } from '../api/request-center.api'
import { RequestStateBadge } from '../components/RequestStateBadge'
import { requestCenterKeys } from '../hooks/query-keys'

/**
 * Admin-wide requests view at `/requests-center/admin/requests`. Unlike
 * `MyRequestsPage` (submitter-scoped — backend forces
 * `filters[submitter] = caller.userId` for non-admins), this page lists
 * **every** request and lets admins filter by the same dimensions the
 * Admin Snapshot tiles surface:
 *
 *   - `?state=approved`             → Provisioning queue (post-approval,
 *                                     ticket lifecycle pending)
 *   - `?staleOnly=true`             → non-final requests submitted > 7d
 *                                     ago (`state=!final` + `to=NOW-7d`)
 *   - `?productiveErrorsOnly=true`  → rows where `metadata.productiveError`
 *                                     is set
 *   - `?state=<any>`                → arbitrary state filter
 *
 * The route is wrapped in `<RequireRole minRole={UserRole.ADMIN}>` at the
 * router level so non-admins never reach this component. Backend admin
 * scope (no `submitter` clamp) is the same enforcement on the data side.
 *
 * Filters land in URL search params so the dashboard tiles can deep-link
 * cleanly and admins can bookmark a particular operational view.
 */
const PAGE_SIZE = 20
const STALE_THRESHOLD_DAYS = 7

const STATE_FILTER_OPTIONS: Array<{ value: RequestState; label: string }> = [
	{ value: RequestState.SUBMITTED, label: 'Submitted' },
	{ value: RequestState.IN_PROGRESS, label: 'In progress' },
	{ value: RequestState.PENDING_INFO, label: 'Waiting on submitter' },
	{ value: RequestState.APPROVED, label: 'Approved (provisioning)' },
	{ value: RequestState.COMPLETED, label: 'Completed' },
	{ value: RequestState.REJECTED, label: 'Rejected' },
	{ value: RequestState.CANCELLED, label: 'Cancelled' },
	{ value: RequestState.FAILED, label: 'Failed' },
]

interface AdminFilters {
	state: string
	priority: string
	submitter: string
	from: string
	to: string
	staleOnly: boolean
	productiveErrorsOnly: boolean
}

const EMPTY_FILTERS: AdminFilters = {
	state: '',
	priority: '',
	submitter: '',
	from: '',
	to: '',
	staleOnly: false,
	productiveErrorsOnly: false,
}

function readFiltersFromUrl(params: URLSearchParams): AdminFilters {
	return {
		state: params.get('state') ?? '',
		priority: params.get('priority') ?? '',
		submitter: params.get('submitter') ?? '',
		from: params.get('from') ?? '',
		to: params.get('to') ?? '',
		staleOnly: params.get('staleOnly') === 'true',
		productiveErrorsOnly: params.get('productiveErrorsOnly') === 'true',
	}
}

function buildListParams(f: AdminFilters, offset: number): ListRequestParams {
	const params: ListRequestParams = { limit: PAGE_SIZE, offset }
	if (f.state) params.state = f.state
	if (f.priority) params.priority = f.priority
	if (f.submitter) params.submitter = f.submitter
	if (f.from) params.from = f.from
	if (f.to) params.to = f.to
	if (f.productiveErrorsOnly) params.productiveErrorsOnly = true
	// "Stale" = non-final requests submitted > N days ago. We express it as
	// `state=!final` + `to=<NOW - 7d ISO>` (upper bound on submittedAt). When
	// the user explicitly picks a state we drop the !final shortcut and
	// honour the explicit choice — admins can still answer "stale completed
	// requests" by picking a state and a `to` date.
	if (f.staleOnly && !f.state) params.state = '!final'
	if (f.staleOnly && !f.to) {
		params.to = new Date(Date.now() - STALE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000).toISOString()
	}
	return params
}

export function AdminRequestsPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const [page, setPage] = useState(0)
	const [filters, setFilters] = useState<AdminFilters>(() => readFiltersFromUrl(searchParams))

	// Re-sync local state when the URL changes (e.g. user clicks another
	// dashboard tile and lands here with different params, or hits Back).
	useEffect(() => {
		setFilters(readFiltersFromUrl(searchParams))
		setPage(0)
	}, [searchParams])

	const params = useMemo(() => buildListParams(filters, page * PAGE_SIZE), [filters, page])

	const { data, isLoading, error, isFetching } = useQuery({
		queryKey: requestCenterKeys.requests.adminList(params),
		queryFn: () => requestCenterApi.listRequests(params),
	})

	const items = data?.data ?? []
	const total = data?.total ?? 0
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

	const updateFilter = <K extends keyof AdminFilters>(key: K, value: AdminFilters[K]) => {
		const next = { ...filters, [key]: value }
		setFilters(next)
		setPage(0)
		// Mirror the change into the URL so the view is bookmarkable + the
		// browser Back button works.
		const nextSearchParams = new URLSearchParams()
		if (next.state) nextSearchParams.set('state', next.state)
		if (next.priority) nextSearchParams.set('priority', next.priority)
		if (next.submitter) nextSearchParams.set('submitter', next.submitter)
		if (next.from) nextSearchParams.set('from', next.from)
		if (next.to) nextSearchParams.set('to', next.to)
		if (next.staleOnly) nextSearchParams.set('staleOnly', 'true')
		if (next.productiveErrorsOnly) nextSearchParams.set('productiveErrorsOnly', 'true')
		setSearchParams(nextSearchParams, { replace: true })
	}

	const clearFilters = () => {
		setFilters(EMPTY_FILTERS)
		setPage(0)
		setSearchParams(new URLSearchParams(), { replace: true })
	}

	const headline = headlineForFilters(filters)

	return (
		<div className="space-y-6">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h1 className="text-2xl font-semibold tracking-tight">All Requests</h1>
					<p className="text-sm text-muted-foreground">
						{headline} · {total} total
					</p>
				</div>
				<Button variant="outline" onClick={clearFilters} disabled={!hasAnyFilter(filters)}>
					Clear filters
				</Button>
			</header>

			<FiltersBar filters={filters} onChange={updateFilter} />

			{isLoading ? (
				<ListSkeleton />
			) : error ? (
				<ErrorState message={error instanceof Error ? error.message : 'Failed to load requests'} />
			) : items.length === 0 ? (
				<EmptyState />
			) : (
				<ul className="space-y-2">
					{items.map((r) => (
						<RequestRow key={r.id} request={r} />
					))}
				</ul>
			)}

			<Pagination page={page} totalPages={totalPages} disabled={isFetching} onChange={setPage} />
		</div>
	)
}

function headlineForFilters(f: AdminFilters): string {
	if (f.staleOnly) return `Stale (non-final, > ${STALE_THRESHOLD_DAYS} days old)`
	if (f.productiveErrorsOnly) return 'Productive errors'
	if (f.state === RequestState.APPROVED) return 'Provisioning (approved, awaiting tickets)'
	if (f.state) return `State: ${f.state}`
	return 'All requests'
}

function hasAnyFilter(f: AdminFilters): boolean {
	return Boolean(f.state || f.priority || f.submitter || f.from || f.to || f.staleOnly || f.productiveErrorsOnly)
}

function FiltersBar({
	filters,
	onChange,
}: {
	filters: AdminFilters
	onChange: <K extends keyof AdminFilters>(key: K, value: AdminFilters[K]) => void
}) {
	return (
		<div className="space-y-3">
			<div className="grid grid-cols-1 gap-3 md:grid-cols-4">
				<select
					className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={filters.state}
					onChange={(e) => onChange('state', e.target.value)}
					aria-label="Filter by state"
				>
					<option value="">All states</option>
					{STATE_FILTER_OPTIONS.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
				<select
					className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={filters.priority}
					onChange={(e) => onChange('priority', e.target.value)}
					aria-label="Filter by priority"
				>
					<option value="">All priorities</option>
					<option value="normal">Normal</option>
					<option value="high">High</option>
					<option value="critical">Critical</option>
				</select>
				<Input
					type="search"
					value={filters.submitter}
					onChange={(e) => onChange('submitter', e.target.value)}
					placeholder="Submitter user id…"
					aria-label="Filter by submitter user id"
				/>
				<div className="flex gap-2">
					<Input
						type="date"
						value={filters.from}
						onChange={(e) => onChange('from', e.target.value)}
						aria-label="Submitted from"
					/>
					<Input
						type="date"
						value={filters.to}
						onChange={(e) => onChange('to', e.target.value)}
						aria-label="Submitted to"
					/>
				</div>
			</div>
			<div className="flex flex-wrap gap-3 text-sm">
				<label className="inline-flex items-center gap-2">
					<input
						type="checkbox"
						checked={filters.staleOnly}
						onChange={(e) => onChange('staleOnly', e.target.checked)}
					/>
					Stale only (non-final, &gt; {STALE_THRESHOLD_DAYS}d old)
				</label>
				<label className="inline-flex items-center gap-2">
					<input
						type="checkbox"
						checked={filters.productiveErrorsOnly}
						onChange={(e) => onChange('productiveErrorsOnly', e.target.checked)}
					/>
					Productive errors only
				</label>
			</div>
		</div>
	)
}

function RequestRow({ request }: { request: RequestDTO }) {
	return (
		<li>
			<Link to={`/requests-center/${request.id}`} className="block focus:outline-none">
				<Card className="flex items-center justify-between gap-3 p-4 transition-colors hover:border-primary/50 focus-visible:border-primary">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<span className="font-mono text-sm font-medium">{request.requestNumber}</span>
							<Badge variant="outline">{request.priority}</Badge>
						</div>
						<p className="text-xs text-muted-foreground">Submitted {new Date(request.submittedAt).toLocaleString()}</p>
					</div>
					<RequestStateBadge state={request.currentState} />
				</Card>
			</Link>
		</li>
	)
}

function Pagination({
	page,
	totalPages,
	disabled,
	onChange,
}: {
	page: number
	totalPages: number
	disabled: boolean
	onChange: (next: number) => void
}) {
	return (
		<nav aria-label="Pagination" className="flex items-center justify-between">
			<p className="text-xs text-muted-foreground">
				Page {page + 1} of {totalPages}
			</p>
			<div className="flex gap-2">
				<Button variant="outline" disabled={disabled || page === 0} onClick={() => onChange(page - 1)}>
					Previous
				</Button>
				<Button variant="outline" disabled={disabled || page + 1 >= totalPages} onClick={() => onChange(page + 1)}>
					Next
				</Button>
			</div>
		</nav>
	)
}

function ListSkeleton() {
	return (
		<ul className="space-y-2">
			{[0, 1, 2, 3].map((i) => (
				<li key={i} className="h-16 animate-pulse rounded-lg border bg-muted/40" />
			))}
		</ul>
	)
}

function EmptyState() {
	return (
		<div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
			No matching requests.
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}
